import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import Icon from '../components/atoms/Icon';
import { verifyResetOTP, resetPassword } from '../services/api';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [userType, setUserType] = useState(searchParams.get('userType') || 'candidate');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState('verify'); // 'verify' or 'reset'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    if (!email || !userType) {
      navigate('/forgot-password');
    }
  }, [email, userType, navigate]);

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setFeedback({ type: 'error', message: 'Please enter a valid 6-digit OTP.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });

    try {
      await verifyResetOTP({ email, otp, userType });
      setFeedback({
        type: 'success',
        message: 'OTP verified successfully! Now set your new password.',
      });
      setStep('reset');
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Invalid or expired OTP. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', message: 'Passwords do not match.' });
      return;
    }

    if (newPassword.length < 6) {
      setFeedback({ type: 'error', message: 'Password must be at least 6 characters long.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });

    try {
      await resetPassword({
        email,
        otp,
        newPassword,
        confirmPassword,
        userType,
      });
      
      setFeedback({
        type: 'success',
        message: 'Password reset successfully! Redirecting to login...',
      });

      setTimeout(() => {
        navigate(`/login?role=${userType}`);
      }, 2000);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Failed to reset password. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatOTP = (value) => {
    return value.replace(/\D/g, '').slice(0, 6);
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon 
                  name={step === 'verify' ? 'verified_user' : 'lock'} 
                  className="w-8 h-8 text-primary" 
                />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {step === 'verify' ? 'Verify OTP' : 'Set New Password'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {step === 'verify' 
                ? `Enter the 6-digit OTP sent to ${email}`
                : 'Create a strong new password for your account'
              }
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <span className="ml-2 text-sm font-medium text-primary">Verify OTP</span>
              </div>
              <div className={`w-12 h-1 ${step === 'reset' ? 'bg-primary' : 'bg-gray-300'} rounded-full`}></div>
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === 'reset' 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  2
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  step === 'reset' ? 'text-primary' : 'text-gray-500'
                }`}>Reset Password</span>
              </div>
            </div>
          </div>

          {/* OTP Verification Form */}
          {step === 'verify' && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enter OTP
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(formatOTP(e.target.value))}
                  className="h-12 text-center text-xl tracking-widest font-mono"
                  maxLength={6}
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter the 6-digit code sent to your email
                </p>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={otp.length !== 6 || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify OTP'
                )}
              </Button>
            </form>
          )}

          {/* Password Reset Form */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12 pr-12"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon name={showPassword ? 'visibility_off' : 'visibility'} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 pr-12"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <Icon name={showConfirmPassword ? 'visibility_off' : 'visibility'} />
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Password Requirements:
                </p>
                <div className="space-y-2">
                  <div className={`flex items-center gap-3 ${newPassword.length >= 6 ? 'text-green-600' : 'text-gray-500'}`}>
                    <Icon name={newPassword.length >= 6 ? 'check_circle' : 'radio_button_unchecked'} className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">At least 6 characters</span>
                  </div>
                  <div className={`flex items-center gap-3 ${newPassword === confirmPassword && newPassword ? 'text-green-600' : 'text-gray-500'}`}>
                    <Icon name={newPassword === confirmPassword && newPassword ? 'check_circle' : 'radio_button_unchecked'} className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">Passwords match</span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 6 || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}

          {/* Feedback Message */}
          {feedback.message && (
            <div
              className={`mt-6 rounded-lg border px-4 py-3 text-sm ${
                feedback.type === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
                  : 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon 
                  name={feedback.type === 'error' ? 'error' : 'check_circle'} 
                  className="w-4 h-4" 
                />
                {feedback.message}
              </div>
            </div>
          )}

          {/* Back to Login */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-primary hover:underline font-medium"
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
